/**
 * device-heal.ts — Device deactivation self-heal handler
 *
 * On receiving a `device_deactivated` WS event the client attempts to
 * re-register its key bundle. The attempt is rate-limited by a PERSISTED
 * per-deviceId backoff (5 min) stored in localStorage, so it survives reloads
 * and is shared across tabs — a previous in-memory once-guard re-fired on every
 * reload / second tab, leaving stale sender devices reappearing in prod.
 *
 * Dependency-injected (storage/now/emit) so the logic is unit-testable without
 * a real browser.
 */

const REREGISTER_BACKOFF_MS = 5 * 60 * 1000; // 5 min
/** Fix (zombie-tab): when a cascade deactivation cannot auto-recover (recovery
 *  rate-limited or failed), force ONE hard reload onto a fresh device rather than
 *  leaving the dead device's tab running — it keeps receiving WS pushes and failing
 *  to decrypt, spamming errors (2026-06-25). Guarded so a device that keeps
 *  cascading can't reload-loop tighter than this window. */
const CASCADE_FORCE_RELOAD_GUARD_MS = 10 * 60 * 1000; // 10 min

export interface HealStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export interface HealDeps {
  deviceId: string;
  reRegister: () => Promise<void>;
  toast: (m: string) => void;
  /** Persistence layer. Defaults to a localStorage wrapper (no-op outside a browser). */
  storage?: HealStorage;
  /** Clock. Defaults to Date.now. */
  now?: () => number;
  /** Forensics hook fired once per attempt. Defaults to noop. */
  emit?: (ev: { deviceId: string; source: string; success: boolean }) => void;
  /** CR1: wipe corrupt ratchet state (clearCorruptRatchetState). When both this
   *  and reinitCrypto are present, a cascade deactivation auto-recovers instead
   *  of showing the manual clear-data alert. */
  clearCorruptState?: () => Promise<void>;
  /** CR1: reset + re-run useCrypto.initialize to mint a fresh device. */
  reinitCrypto?: () => Promise<void>;
  /** Zombie-tab stopper: clear the persisted deviceId and hard-reload so init
   *  mints a fresh device. Fired (guarded) when a cascade deactivation cannot
   *  auto-recover, instead of leaving the dead device's tab spamming decrypt
   *  failures. Injected so it stays unit-testable (no real window.location). */
  forceReload?: () => void;
}

const defaultLocalStorage: HealStorage = {
  get: (k) => (typeof localStorage !== "undefined" ? localStorage.getItem(k) : null),
  set: (k, v) => {
    if (typeof localStorage !== "undefined") localStorage.setItem(k, v);
  },
};

/**
 * Handle a `device_deactivated` WS payload: re-register at most once per
 * `REREGISTER_BACKOFF_MS` per device, else prompt the user to clear data.
 */
export async function handleDeviceDeactivated(
  payload: { reason: string },
  deps: HealDeps,
): Promise<void> {
  if (payload.reason === "cascade") {
    // CR1: a cascade-dead device cannot be re-registered (corrupt local
    // ratchet — re-register would resurrect a zombie). If the live recovery
    // deps are wired, auto-recover ONCE per 6h: wipe the corrupt state + mint a
    // fresh deviceId. Otherwise (or on rate-limit / failure) fall back to the
    // manual clear-data alert.
    if (deps.clearCorruptState && deps.reinitCrypto) {
      const { handleCascadeRecovery } = await import("../lib/cascade-recovery");
      const result = await handleCascadeRecovery({
        deviceId: deps.deviceId,
        clearCorruptState: deps.clearCorruptState,
        reinitCrypto: deps.reinitCrypto,
        toast: deps.toast,
        storage: deps.storage,
        now: deps.now,
        emit: (ev) => (deps.emit ?? (() => {}))({
          deviceId: ev.deviceId, source: "cascade_recovery", success: ev.outcome === "recovered",
        }),
      });
      if (result.outcome === "recovered") return; // toast already fired by recovery
      // fallthrough → recovery rate-limited or failed
    }
    // Zombie-tab stopper: the dead device's tab keeps receiving WS pushes and
    // failing to decrypt. If a forceReload is wired and we have not already reloaded
    // within the guard window, drop the persisted deviceId + hard-reload onto a fresh
    // device instead of leaving the tab spamming errors. Guarded so a device that
    // keeps cascading can't reload-loop.
    {
      const storage = deps.storage ?? defaultLocalStorage;
      const now = (deps.now ?? Date.now)();
      const reloadKey = "kp_cascade_forcereload_at";
      const lastParsed = parseInt(storage.get(reloadKey) ?? "0", 10);
      const lastReload = Number.isNaN(lastParsed) ? 0 : lastParsed;
      if (deps.forceReload && now - lastReload > CASCADE_FORCE_RELOAD_GUARD_MS) {
        storage.set(reloadKey, String(now));
        (deps.emit ?? (() => {}))({ deviceId: deps.deviceId, source: "cascade_forcereload", success: false });
        deps.toast("Dieses Gerät wird zurückgesetzt (Verschlüsselungsfehler)…");
        deps.forceReload();
        return;
      }
    }
    (deps.emit ?? (() => {}))({ deviceId: deps.deviceId, source: "cascade", success: false });
    deps.toast(
      "Dieses Gerät hat einen Verschlüsselungsfehler. Bitte abmelden, Browser-Daten für app.kollegeplus.de löschen und neu anmelden.",
    );
    return;
  }

  const key = `kp_reregister_at:${deps.deviceId}`;
  const now = (deps.now ?? Date.now)();
  const storage = deps.storage ?? defaultLocalStorage;
  const emit = deps.emit ?? (() => {});

  const parsed = parseInt(storage.get(key) ?? "0", 10);
  const last = Number.isNaN(parsed) ? 0 : parsed;

  if (now - last < REREGISTER_BACKOFF_MS) {
    // Backoff active — a recent attempt (this tab, another tab, or before a
    // reload) already fired. Don't hammer re-registration.
    deps.toast(
      "Reaktivierung läuft bereits oder kürzlich versucht. Falls das Problem bleibt: abmelden, Browser-Daten für app.kollegeplus.de löschen und neu anmelden.",
    );
    return;
  }

  // Claim the slot BEFORE awaiting so a concurrent second tab sees the backoff.
  storage.set(key, String(now));
  deps.toast("Dieses Gerät wurde abgemeldet — versuche Reaktivierung…");

  try {
    await deps.reRegister();
    emit({ deviceId: deps.deviceId, source: payload.reason, success: true });
  } catch {
    emit({ deviceId: deps.deviceId, source: payload.reason, success: false });
    deps.toast(
      "Reaktivierung fehlgeschlagen. Bitte abmelden, Browser-Daten löschen und neu anmelden.",
    );
  }
}
